import './Provider.scss';

import * as React from 'react';
import * as _ from 'lodash';

import {
  Breadcrumb,
  IDropdownOption,
  IPersonaSharedProps,
  IconButton,
  Label,
  Link,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  Pivot,
  PivotItem,
  Spinner,
  SpinnerSize,
  Stack,
} from '@fluentui/react';
import { VisitStatus } from '../../types';

import { AsyncData } from '../../redux/core';
import { AsyncVisitSidePanel } from '../core/AsyncVisitSidePanel';
import { InlineEditField } from '../core/InlineEditField';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { PageSidePanel } from '../core/PageSidePanel';
import { PieChart } from 'react-minimal-pie-chart';
import { ProviderAddressDialog } from './ProviderAddressDialog';
import { ProviderAddress, ProviderData } from '../../api/providers.types';
import { ROUTES } from '../../config/routes';
import { ToastParams } from '../../redux/message/types';
import { Visit } from '../../redux/visits/types';
import { VisitList } from '../core/VisitList';
import { getIconDetails } from '../visit/VisitStatusIcon';
import { isNPIValid } from '../../util/String';
import { formatPhoneNumber } from '../../util/PhoneNumber';
import { DocumentTitle } from '../core/DocumentTitle';
import { ServiceOffering } from '../../api/services.types';
import { Market } from '../../api/markets.types';
import { HealHqMap } from '../core/HealHqMap';
import { visitSearchByAssistantId, visitSearchByDoctorId } from '../../api/elasticsearch';
import { ProviderPatients } from './ProviderPatients';
import { updateProvider, fetchProvider as fetchProviderApi } from '../../api/providers';
import { ProviderScheduleList } from './ProviderScheduleList';
import CopyButton from '../core/CopyButton';
import { NavigateFunction } from 'react-router-dom';
import ProviderPod from '../pod/ProviderPod';

export interface OwnProps {
  params?: any;
  navigate: NavigateFunction;
}

export interface StateProps {
  visitCode: string;
  markets: AsyncData<Market[]>;
  services: AsyncData<ServiceOffering[]>;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setVisitCode: (visitCode: string) => void;
    toast: (params: ToastParams) => void;
  };
}

type ProviderProps = OwnProps & StateProps & DispatchProps;

export interface ProviderState {
  error?: string;
  model: AsyncData<{ provider: ProviderData }>;
  visits: AsyncData<Visit[]>;
  markets: IDropdownOption[];
  services: IDropdownOption[];
  selectedMarkets: string[];
  selectedMarketsPD: string[];
  selectedServices: string[];
  addresses: ProviderAddress[];
}

export class Provider extends React.Component<ProviderProps, ProviderState> {
  constructor(props: ProviderProps) {
    super(props);

    let markets: IDropdownOption[] = [];
    if (props.markets && props.markets.data) {
      markets = props.markets.data.map((item) => ({ key: item.code, text: `${item.name} (${item.code})` }));
    }

    let services: IDropdownOption[] = [];
    if (props.services && props.services.data) {
      services = props.services.data.map((item) => ({ key: item.id, text: item.name }));
    }

    this.state = {
      model: { isFetching: false },
      visits: { isFetching: false },
      markets,
      services,
      selectedMarkets: [],
      selectedMarketsPD: [],
      selectedServices: [],
      addresses: [],
    };
  }

  public componentDidUpdate(prevProps: ProviderProps) {
    if (this.props.markets !== prevProps.markets && this.props.markets.data) {
      const markets = this.props.markets.data.map((item) => ({ key: item.code, text: `${item.name} (${item.code})` }));
      this.setState({ markets });
    }
    if (this.props.services !== prevProps.services && this.props.services.data) {
      const services = this.props.services.data.map((item) => ({ key: item.id, text: item.name }));
      this.setState({ services });
    }
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Provider');
    this.fetchProvider(this.props.params.id);
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Provider');
  }

  public render(): JSX.Element {
    if (this.state.error) {
      return (
        <div style={{ marginTop: 20 }}>
          <MessageBar messageBarType={MessageBarType.error}>{this.state.error}</MessageBar>
        </div>
      );
    }

    return (
      <div className="provider-page">
        <DocumentTitle title={this.state.model.data?.provider.lastName || 'Loading...'} />
        <Breadcrumb
          items={[
            { text: 'Providers', key: 'providers', onClick: this.onProvidersClick },
            { text: this.getName(), key: 'provider', isCurrentItem: true },
          ]}
          // Returning undefined to OnReduceData tells the breadcrumb not to shrink
          onReduceData={() => undefined}
          data-tid="txt_title"
        />
        <br />
        {this.state.model.isFetching && <Spinner size={SpinnerSize.large} label="Loading provider..." />}
        {this.renderPersona()}
        <Pivot onLinkClick={this.onPivotLinkClick} style={{ marginTop: 10 }} selectedKey={this.props.params.tab || 'profile'}>
          <PivotItem headerText="Profile" itemKey="profile">
            {this.renderDetails()}
            <div className="flex flex-row metrics">
              {this.renderLastKnownLocation()}
              {this.renderVisitBreakdown()}
            </div>
            {this.renderStatusAction()}
          </PivotItem>
          <PivotItem headerText="Visits" itemKey="visits">
            {this.renderVisits()}
            {this.renderSidePanel()}
          </PivotItem>
          {this.state.model.data?.provider.providerType === 'DOCTOR' && (
            <PivotItem headerText="Patients" itemKey="patients">
              <ProviderPatients providerId={this.props.params.id} />
            </PivotItem>
          )}
          <PivotItem headerText="Schedule" itemKey="schedule">
            <ProviderScheduleList providerId={this.props.params.id} />
          </PivotItem>
          <PivotItem headerText="Pods" itemKey="pods">
            <ProviderPod providerId={this.props.params.id} />
          </PivotItem>
        </Pivot>
      </div>
    );
  }

  private onPivotLinkClick = (item?: PivotItem) => {
    this.props.actions.setVisitCode('');
    this.props.navigate(`${ROUTES.providers}/${this.props.params.id}/${item!.props.itemKey!}`);
  };

  private renderStatusAction(): JSX.Element | null {
    const data = this.state.model.data;
    if (!data) {
      return null;
    }

    const action: string = data.provider.active ? 'Deactivate' : 'Activate';

    return (
      <Link
        onClick={() =>
          OkCancelDialog.show({
            title: 'Are you sure you want to ' + action.toLowerCase() + ' this provider?',
            onOk: () => {
              this.onSubmit({ active: !data.provider.active });
            },
          })
        }
      >
        {action + ' Provider'}
      </Link>
    );
  }

  private renderPersona(): JSX.Element | null {
    const data = this.state.model.data;
    const formattedPhone = data ? formatPhoneNumber(data.provider.phone) : '';

    if (!data) {
      return null;
    }

    const persona: IPersonaSharedProps = {
      imageUrl: data.provider.avatarUrl,
      imageInitials: data.provider.firstName[0] + data.provider.lastName[0],
      text: data.provider.fullName,
      secondaryText: data.provider.email,
      tertiaryText: formattedPhone,
      optionalText: `Member since: ${data.provider.memberSince}`,
    };

    return <Persona {...persona} size={PersonaSize.size100} />;
  }

  private renderLastKnownLocation(): JSX.Element | null {
    const data: any = this.state.model.data; // TODO: type lat/long/time params
    if (!data) {
      return null;
    }

    const point = { lat: data.lastKnownLatitude, lng: data.lastKnownLongitude };

    return (
      <div>
        <div className="section-title">Last known location</div>
        <HealHqMap height={200} center={point} points={[point]} />
        <div className="section-footer">At: {data.lastKnownLocationTime}</div>
      </div>
    );
  }

  private renderVisitBreakdown(): JSX.Element | null {
    const visits = this.state.visits.data;
    if (!visits || this.state.visits.error) {
      return null;
    }
    const groups = _.groupBy(visits, 'status');
    const chartData = Object.keys(groups).map((k) => ({
      title: k,
      value: groups[k].length,
      color: getIconDetails(k).color,
    }));

    return (
      <div style={{ marginLeft: 10 }}>
        <div className="section-title">Visit breakdown</div>
        <div className="rollup">
          <div className="rollup-chart">
            <PieChart lineWidth={50} animate={true} totalValue={visits.length} data={chartData} />
            <div className="total">{visits.length}</div>
          </div>
          <div className="rollup-details">
            {Object.keys(VisitStatus).map((status, index) => {
              const v = groups[status];
              if (!v || v.length === 0) {
                return null;
              }
              return (
                <div className="legend" key={index}>
                  <div className="block" style={{ backgroundColor: getIconDetails(status).color }} />
                  <div className="title" style={{ color: getIconDetails(status).color }}>
                    {v.length} - {status}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  private renderDetails(): JSX.Element | null {
    const data = this.state.model.data;
    if (!data) {
      return null;
    }

    let npiWarning: JSX.Element | null = null;
    if (data.provider.providerType === 'DOCTOR' && !isNPIValid(data.provider.providerNPI)) {
      npiWarning = <MessageBar messageBarType={MessageBarType.severeWarning}>The NPI number for this provider does not appear valid. Please check.</MessageBar>;
    }

    let wiwWarning: JSX.Element | null = null;
    if (!data.provider.wiwUserId) {
      wiwWarning = <MessageBar messageBarType={MessageBarType.blocked}>There is no When I Work ID for this provider.</MessageBar>;
    }

    return (
      <div className="details">
        {data.provider.providerType === 'DOCTOR' && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
            <div>
              <Label>Provider NPI</Label>
              <span>{data.provider.providerNPI}</span>
            </div>
            <CopyButton text={data.provider.providerNPI} message="Provider NPI has been copied to clipboard" />
          </Stack>
        )}
        {npiWarning}
        {this.renderEditableField('Photo', ['avatarUrl'], [data.provider.avatarUrl ?? ''], undefined, true)}
        {this.renderEditableField('Video', ['bioVideoUrl'], [data.provider.bioVideoUrl ?? ''], undefined, true)}
        {this.renderEditableField('Video Poster Frame', ['bioVideoPosterFrameUrl'], [data.provider.bioVideoPosterFrameUrl ?? ''], undefined, true)}
        {this.renderEditableField('When I Work ID', ['wiwUserId'], [data.provider.wiwUserId], undefined, false, false, true)}
        {wiwWarning}
        {this.renderEditableField('Phone', ['phone'], [data.provider.phone ?? ''], undefined, false, false)}
        {this.renderProviderAddress()}
        <div style={{ whiteSpace: 'pre' }}>
          {this.renderEditableField('Bio', ['bio'], [data.provider.bio ?? ''])}
          {this.renderEditableField('Education', ['education'], [data.provider.education ?? ''])}
        </div>
        {this.state.markets.length > 0 && this.renderEditableField('Markets', ['markets'], this.state.selectedMarkets, this.state.markets)}
        {this.state.markets.length > 0 &&
          data.provider.providerType === 'DOCTOR' &&
          this.renderEditableField(
            'Preferred Doctor Eligible Markets',
            ['pdMarkets'],
            this.state.selectedMarketsPD,
            this.state.markets.filter((m) => this.state.selectedMarkets.indexOf(m.key as string) > -1)
          )}
        {this.state.services.length > 0 && this.renderEditableField('Services', ['services'], this.state.selectedServices, this.state.services)}
      </div>
    );
  }

  private renderVisits(): JSX.Element | null {
    if (!this.state.model.data) {
      return null;
    }

    if (this.state.visits.error) {
      return (
        <div style={{ marginTop: 20 }}>
          <MessageBar messageBarType={MessageBarType.error}>{this.state.visits.error}</MessageBar>
        </div>
      );
    }

    const columns = ['insurance', 'code', 'status', 'service', 'date', 'duration', 'patient', 'address'];
    if (this.state.model.data.provider.providerType === 'DOCTOR') {
      columns.push('assistant');
    } else {
      columns.push('doctor');
    }

    return (
      <VisitList
        isFetching={this.state.visits.isFetching}
        visits={this.state.visits.data || []}
        columns={columns}
        markets={this.props.markets}
        onSelected={this.onSelected}
        selectedVisitCode={this.props.visitCode}
      />
    );
  }

  private renderProviderAddress(): JSX.Element {
    const data = this.state.addresses;
    return (
      <div>
        <Label>Home Address</Label>
        {data.map((a, index) => {
          const address = a.addressLine1 + ', ' + a.city + ' ' + a.state + ' ' + a.country + ' ' + a.zipcode;
          return (
            <div key={index}>
              <div>{address}</div>
              <div>{a.addressLine2 != null && 'Unit: ' + a.addressLine2}</div>
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                onClick={() =>
                  ProviderAddressDialog.show({
                    providerId: this.state.model.data!.provider.doctorId,
                    address: a,
                    onEditAddress: (address) => {
                      this.onSubmit({ addressRequest: address });
                    },
                  })
                }
              />
            </div>
          );
        })}
        {data.length === 0 && (
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            onClick={() =>
              ProviderAddressDialog.show({
                providerId: this.state.model.data!.provider.doctorId,
                onEditAddress: (address) => {
                  this.onSubmit({ addressRequest: address });
                },
              })
            }
          />
        )}
      </div>
    );
  }

  private renderEditableField(
    label: string,
    fieldNames: string[],
    values: string[],
    options?: IDropdownOption[],
    showLink?: boolean,
    multiline = true,
    readonly = false
  ): JSX.Element | null {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Label>{label}</Label>
          {showLink && values[0] && (
            <IconButton iconProps={{ iconName: 'NavigateExternalInline' }} title="Open in new tab" ariaLabel="Open in new tab" onClick={() => window.open(values[0], '_blank')} />
          )}
        </div>
        <InlineEditField
          preventSubmitOnEnter={multiline}
          fieldNames={fieldNames}
          initialValues={values}
          multiline={multiline}
          readonly={readonly}
          options={options}
          onSubmit={(newValue) => this.onSubmit(newValue)}
        />
      </>
    );
  }

  private onSubmit = (newValue: any): any => {
    if (Object.keys(newValue).indexOf('markets') > -1) {
      const { selectedMarketsPD } = this.state;
      const removedMarket = selectedMarketsPD.find((m) => newValue.markets.indexOf(m) === -1);
      if (removedMarket) {
        if (
          window.confirm(
            'Removing ' +
              removedMarket +
              ' market from this list will also remove it from the Preferred Doctor Eligible Markets list. This means patients in the ' +
              removedMarket +
              ' market will not be able to select this provider as their preferred doctor. Are you sure you want to continue?'
          )
        ) {
          const indexOfRemovedMarket = selectedMarketsPD.indexOf(removedMarket);
          if (indexOfRemovedMarket > -1) {
            selectedMarketsPD.splice(indexOfRemovedMarket, 1);
            this.setState({ selectedMarketsPD });
          }
        } else {
          return false;
        }
      }
    }
    if (newValue.phone && !/\+1\d{10}$/.test(newValue.phone)) {
      this.props.actions.toast({
        type: 'error',
        content: 'Phone number must be in the format +1##########',
      });
      return Promise.resolve(false);
    }
    return updateProvider(this.props.params.id, newValue)
      .then((data) => {
        this.props.actions.toast({
          type: 'success',
          title: 'Success',
          content: "Provider's profile has been successfully updated.",
        });
        if (Object.keys(newValue).indexOf('markets') > -1) {
          this.setState({ selectedMarkets: newValue.markets });
        }
        if (Object.keys(newValue).indexOf('marketsPD') > -1) {
          this.setState({ selectedMarketsPD: newValue.marketsPD });
        }

        this.setState({
          model: {
            ...this.state.model,
            data: {
              ...this.state.model.data,
              provider: { ...this.state.model.data!.provider, ...data },
            },
          },
          addresses: data.addressResponses,
        });

        return true;
      })
      .catch(() => false);
  };

  private renderSidePanel(): JSX.Element | null {
    if (!this.props.visitCode) {
      return null;
    }
    return (
      <PageSidePanel>
        <AsyncVisitSidePanel visitCode={this.props.visitCode} />
      </PageSidePanel>
    );
  }

  private onSelected = (visitCode: string) => {
    this.props.actions.setVisitCode(visitCode);
  };

  private onProvidersClick = () => {
    this.props.navigate(ROUTES.providers);
  };

  private getName(): string {
    if (this.state.model.data) {
      return this.state.model.data.provider.fullName;
    }
    return 'Loading...';
  }

  private fetchProvider(providerId: string) {
    this.setState({ model: { isFetching: true } });
    fetchProviderApi(providerId)
      .then((data: any) => {
        this.setState({
          selectedMarkets: data.provider.marketCodes ? data.provider.marketCodes.slice() : [],
          selectedMarketsPD: data.provider.pdMarkets ? data.provider.pdMarkets.slice() : [],
          selectedServices: data.provider.serviceIds ? data.provider.serviceIds.slice() : [],
          addresses: data.provider.addressResponses ? data.provider.addressResponses.slice() : [],
          model: { data, isFetching: false },
        });
        this.fetchProviderVisits(providerId, data.provider.providerType);
      })
      .catch((error) => {
        this.setState({ error: error.message, model: { isFetching: false } });
      });
  }

  private fetchProviderVisits(providerId: string, providerType: string) {
    this.setState({ visits: { isFetching: true } });
    const promise = providerType === 'DOCTOR' ? visitSearchByDoctorId(providerId) : visitSearchByAssistantId(providerId);
    promise.then(
      (data: any[]) => {
        this.setState({ visits: { isFetching: false, data } });
      },
      () => {
        this.setState({ visits: { isFetching: false, error: 'Failed to retrieve visits for the provider.' } });
      }
    );
  }
}
